import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plane, Mail, Lock, User, AlertCircle } from "lucide-react";
import DroneIcon from "../components/DroneIcon";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Auth({ mode = "login" }) {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { loginWithPassword, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isRegister = mode === "register";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isRegister) {
        await register(email, password, name);
        toast.success("Hoş geldiniz! Hesabınız oluşturuldu.");
      } else {
        await loginWithPassword(email, password);
        toast.success("Giriş başarılı.");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bir hata oluştu.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#C29B62]/20 blur-[120px] rounded-full" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#7a5e34]/20 blur-[120px] rounded-full" />
      </div>

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#C29B62] to-[#7a5e34] flex items-center justify-center">
            <DroneIcon size={16} className="text-slate-950" />
          </div>
          <span className="font-display font-black text-lg">ProSanalDrone</span>
        </Link>

        <div className="rounded-2xl bg-slate-900/60 backdrop-blur-xl border border-white/10 p-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[#C29B62] mb-3">
            {isRegister ? "Yeni Hesap" : "Hoş Geldin"}
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-white mb-8">
            {isRegister ? "Ücretsiz Hesap Oluştur" : "Hesabına giriş yap"}
          </h1>

          {sp.get("error") === "auth" && (
            <div className="flex items-start gap-2 p-3 mb-6 rounded-md bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              Google ile giriş başarısız. Lütfen tekrar deneyin.
            </div>
          )}

          <button data-testid="google-auth-btn" onClick={handleGoogle}
            className="w-full py-3 rounded-md bg-white text-slate-900 font-medium hover:bg-slate-100 transition flex items-center justify-center gap-3">
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Google ile {isRegister ? "kayıt ol" : "giriş yap"}
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-slate-500 uppercase tracking-[0.2em]">veya</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 block">Ad Soyad</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input data-testid="auth-name-input" required value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full bg-slate-950/60 border border-white/10 rounded-md pl-10 pr-3 py-3 text-slate-100 placeholder:text-slate-600 focus:border-[#C29B62] focus:outline-none focus:ring-1 focus:ring-[#C29B62]"
                    placeholder="Ahmet Yılmaz" />
                </div>
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 block">E-posta</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid="auth-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-950/60 border border-white/10 rounded-md pl-10 pr-3 py-3 text-slate-100 placeholder:text-slate-600 focus:border-[#C29B62] focus:outline-none focus:ring-1 focus:ring-[#C29B62]"
                  placeholder="mail@ornek.com" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 block">Şifre</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid="auth-password-input" required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-slate-950/60 border border-white/10 rounded-md pl-10 pr-3 py-3 text-slate-100 placeholder:text-slate-600 focus:border-[#C29B62] focus:outline-none focus:ring-1 focus:ring-[#C29B62]"
                  placeholder="••••••••" minLength={6} />
              </div>
            </div>

            <button type="submit" data-testid="auth-submit-btn" disabled={submitting}
              className="w-full py-3 rounded-md bg-[#C29B62] text-slate-950 font-semibold hover:bg-[#D4AF7A] transition disabled:opacity-50">
              {submitting ? "Lütfen bekleyin..." : (isRegister ? "Hesap Oluştur" : "Giriş Yap")}
            </button>
          </form>

          <div className="text-center text-sm text-slate-400 mt-6">
            {isRegister ? (
              <>Zaten hesabın var mı? <Link to="/login" data-testid="switch-to-login" className="text-[#C29B62] hover:underline">Giriş yap</Link></>
            ) : (
              <>Hesabın yok mu? <Link to="/register" data-testid="switch-to-register" className="text-[#C29B62] hover:underline">Kayıt ol</Link></>
            )}
          </div>

          {!isRegister && (
            <div className="mt-4 p-3 rounded-md bg-slate-950/40 border border-white/5 text-xs text-slate-400">
              <div className="uppercase tracking-[0.2em] text-slate-500 mb-1">Demo hesap</div>
              demo@prosanaldrone.com / Demo1234!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
